import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Languages } from "lucide-react";
import { useConfirm } from "../../components/confirmDialog";

const LANGUAGES = [
  { code: "hi", name: "Hindi" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "es", name: "Spanish" },
  { code: "ja", name: "Japanese" },
];

const hasOverview = (entry) =>
  Boolean(
    entry &&
      ["tagline", "whyItMatters", "ideas", "curatedQuotes", "actionableInsights", "shouldReadFor", "reflectionQuestions"].some(
        (f) => entry[f]
      )
  );

/**
 * Translate this summary into ONE language at a time — Hindi first, check it,
 * then choose the others. Uses the "Translation" settings in AI Settings.
 *
 * Translates the SAVED English and writes that one language straight away, so
 * it shows in the app immediately. The translated language is merged into the
 * form too, so a later Update can't put the old text back.
 */
const PART_LABELS = { overview: "Overview", breakdown: "Breakdown", both: "Both" };

const TranslatePanel = ({ summaryId, token, translations, onTranslated, onView }) => {
  const [language, setLanguage] = useState("hi");
  // The Overview and the Breakdown are translated SEPARATELY. They are two
  // pieces of writing that happen to share a document, and translating one
  // must never put the other through a model.
  const [part, setPart] = useState("overview");
  // Off by default: only what the English has changed since this language was
  // last translated. On, it redoes that part from scratch.
  const [full, setFull] = useState(false);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const [done, setDone] = useState(null);

  const name = LANGUAGES.find((l) => l.code === language)?.name;

  const translate = async () => {
    const existing = translations?.find((t) => t.langCode === language);
    // Only a FULL redo replaces work that is already there. The default leaves
    // every up-to-date field and chapter exactly as it is, so asking whether
    // to replace them would be asking about something that cannot happen.
    if (
      full &&
      hasOverview(existing) &&
      !(await confirm({
        title: `Redo the whole ${name} ${PART_LABELS[part].toLowerCase()}?`,
        body: "What is already translated will be replaced.",
        confirmLabel: "Redo",
        tone: "primary",
      }))
    ) {
      return;
    }
    setBusy(true);
    setDone(null);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_ROOT}/api/admin/summary/translate/${summaryId}`,
        { language, part, full },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.status) {
        // "Only what changed" can correctly do nothing, and then there is no
        // entry to merge — handing the form a null one crashed it on
        // `entry.chapters`.
        if (data.data?.entry) {
          onTranslated(language, data.data.entry, data.data.aboutTheBook);
        }
        setDone(language);
        toast.success(`${data.message} It's live — check it in the app in ${name}.`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || `Could not translate into ${name}.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border rounded p-3 mb-3" style={{ borderColor: "var(--ps-border)" }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <Languages size={16} />
        <strong>Translate</strong>
        <small className="text-muted">
          Translates the <em>saved</em> English into one language — save your English changes first.
        </small>
      </div>
      <div className="d-flex flex-wrap align-items-center gap-2">
        <select
          className="form_control"
          style={{ width: "auto" }}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled={busy}
          aria-label="Language"
        >
          {LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name}
              {hasOverview(translations?.find((t) => t.langCode === l.code)) ? " — translated" : " — not yet"}
            </option>
          ))}
        </select>
        <div className="btn-group" role="group" aria-label="What to translate">
          {Object.entries(PART_LABELS).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`btn btn-sm ${part === value ? "btn-dark" : "btn-outline-secondary"}`}
              onClick={() => setPart(value)}
              disabled={busy}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="d-flex align-items-center gap-1 mb-0 small text-muted">
          <input
            type="checkbox"
            className="form-check-input mt-0"
            checked={full}
            onChange={(e) => setFull(e.target.checked)}
            disabled={busy}
          />
          Redo everything in it
        </label>
        <button type="button" className="btn btn-sm btn-dark" onClick={translate} disabled={busy || !summaryId}>
          {busy && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />}
          {busy ? `Translating into ${name}…` : `Translate into ${name}`}
        </button>
        {done && !busy && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onView(done)}>
            Review {LANGUAGES.find((l) => l.code === done)?.name} here
          </button>
        )}
      </div>
      <small className="text-muted d-block mt-2">
        {part === "overview"
          ? "Overview: About the Book, Why This Book Matters, Key Ideas, Notable Quotes, Actionable Insights, Who Should Read It, Questions — and the tagline. The chapters are not touched."
          : part === "breakdown"
            ? "Breakdown: the chapters, and nothing from the Overview."
            : "Overview and Breakdown together."}
        {full
          ? " Redoing everything in it, whether or not the English has changed."
          : " Only the parts whose English has been edited since this language was last translated — edit one chapter, translate one chapter."}
      </small>
    </div>
  );
};

export default TranslatePanel;
